import { motion } from "framer-motion";
import { ArrowUpRight, Calendar, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { useCollection } from "@/hooks/useCollection";
import { Kicker, Reveal, SectionTitle, staggerContainer, staggerItem } from "./Reveal";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function parseDate(d) {
  const dt = new Date(`${d}T00:00:00`);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function Wrap({ to, children, ...rest }) {
  if (!to) return <div {...rest}>{children}</div>;
  if (to.startsWith("http"))
    return (
      <a href={to} target="_blank" rel="noreferrer" {...rest}>
        {children}
      </a>
    );
  return (
    <Link to={to} {...rest}>
      {children}
    </Link>
  );
}

function EventRow({ e, i, past }) {
  const dt = parseDate(e.date);
  return (
    <motion.li variants={staggerItem}>
      <Wrap
        to={e.link}
        className={`group grid gap-5 rounded-[10px] border border-line bg-surface p-5 transition-[transform,box-shadow,border-color] duration-500 hover:-translate-y-1 hover:border-pine/40 hover:shadow-[0_20px_40px_rgba(18,33,27,0.1)] md:grid-cols-[96px_1fr_auto] md:items-center md:gap-8 md:p-6 ${
          past ? "opacity-70" : ""
        }`}
        data-testid={`event-${i}`}
        data-cursor={e.link ? "hover" : undefined}
      >
        <div
          className={`flex h-[96px] w-[96px] flex-col items-center justify-center rounded-md font-display transition-colors duration-500 ${
            past ? "bg-bone text-pine" : "bg-pine text-white group-hover:bg-moss"
          }`}
        >
          {dt ? (
            <>
              <span className="text-3xl font-bold leading-none">{dt.getDate()}</span>
              <span className="mt-1 text-xs font-semibold uppercase tracking-[0.16em]">
                {MONTHS[dt.getMonth()]} {String(dt.getFullYear()).slice(2)}
              </span>
            </>
          ) : (
            <Calendar className="h-7 w-7" />
          )}
        </div>
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full bg-bone px-3 py-1 font-display text-xs font-semibold uppercase tracking-[0.12em] text-pine">
              {e.type || "Event"}
            </span>
            {past && <span className="font-display text-xs font-semibold uppercase tracking-[0.12em] text-muted">Past</span>}
          </div>
          <h3 className="mt-3 font-display text-xl font-semibold text-ink md:text-2xl">{e.title}</h3>
          {e.description && <p className="mt-2 max-w-2xl leading-relaxed text-body">{e.description}</p>}
          <p className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-muted">
            {e.time && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" /> {e.time}
              </span>
            )}
            {e.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {e.location}
              </span>
            )}
          </p>
        </div>
        {e.link && (
          <ArrowUpRight className="hidden h-6 w-6 text-pine transition-transform duration-500 group-hover:-translate-y-1 group-hover:translate-x-1 md:block" />
        )}
      </Wrap>
    </motion.li>
  );
}

export default function EventsSection({ limit, showAllLink = false, tint = false }) {
  const { items, visible, loading } = useCollection("events");
  if (loading || !visible || items.length === 0) return null;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcoming = items.filter((e) => {
    const d = parseDate(e.date);
    return !d || d >= today;
  });
  const past = items.filter((e) => !upcoming.includes(e)).sort((a, b) => (a.date < b.date ? 1 : -1));
  const list = [...upcoming, ...past];
  const shown = limit ? list.slice(0, limit) : list;

  return (
    <section className={tint ? "bg-bone" : "bg-surface"} data-testid="events-section">
      <div className="mx-auto max-w-[1200px] px-5 py-20 md:py-28">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-3xl">
            <Kicker>Events &amp; news</Kicker>
            <SectionTitle>Upcoming sessions, workshops and what's new.</SectionTitle>
          </div>
          {showAllLink && (
            <Link
              to="/programs#events"
              className="link-line inline-flex items-center gap-2 font-display font-semibold text-pine"
              data-testid="events-view-all"
            >
              Full calendar <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </Reveal>
        <motion.ul
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-40px" }}
          className="mt-12 grid gap-4"
          data-testid="events-list"
        >
          {shown.map((e, i) => (
            <EventRow key={e.id} e={e} i={i} past={past.includes(e)} />
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
